// Profile V2 (2026-10-19) — presentation-only building blocks for the Profile screen. No data access,
// no business rules: every value and every handler comes from Profile.jsx, which keeps its existing
// state, validation and save APIs. Colours come from the shared tokens (tailwind.config.js); the few
// the token set does not name (long-run day, injury state, HR zones) live in PROFILE_COLORS below (Graphite),
// local to this screen, so no shared global style changes.
import React, { useId, useState } from 'react';

export const PROFILE_COLORS = {
  longRun: 'var(--g-accent)', onLongRun: 'var(--g-on-accent)',
  ok: 'var(--g-accent)', warn: 'var(--g-sub)', danger: 'var(--g-sub)',
  dayDisabled: 'var(--g-bar)', switchOff: 'var(--g-bar)', avatar: 'var(--g-pressed)',
  zones: ['var(--g-bar)', 'var(--g-muted)', 'var(--g-sub)', 'var(--g-text)', 'var(--g-accent)'],
};

export const DAYS = [
  { key: 'ראשון', label: 'א' }, { key: 'שני', label: 'ב' }, { key: 'שלישי', label: 'ג' },
  { key: 'רביעי', label: 'ד' }, { key: 'חמישי', label: 'ה' }, { key: 'שישי', label: 'ו' },
  { key: 'שבת', label: 'ש' },
];

export const CARD = 'rounded-[16px] bg-surface border border-border p-4';
export const INPUT = 'w-full h-12 rounded-[12px] bg-surface-2 border px-3 text-[16px] text-text ' +
  'focus:outline-none focus:ring-2 focus:ring-accent';
export const LABEL = 'block text-[13px] text-text-muted mb-1.5';
export const HELPER = 'text-[12px] text-text-muted leading-snug';

export function SectionLabel({ children, id }) {
  return <h2 id={id} className="text-[13px] font-semibold text-text-muted mb-2 px-1">{children}</h2>;
}

export function CardTitle({ children, aside }) {
  return (
    <div className="flex items-baseline justify-between gap-3 mb-3">
      <h2 className="text-[17px] font-bold text-text">{children}</h2>
      {aside && <span className="text-[12px] text-text-muted">{aside}</span>}
    </div>
  );
}

/** Seven day chips (א…ש). Presentation only — the caller owns selection and its rules. */
export function DayChips({ selected, disabled = () => false, onToggle, tone = 'run', ariaPrefix }) {
  return (
    <div className="grid grid-cols-7 gap-[5px]" role="group">
      {DAYS.map((d) => {
        const on = selected(d.key);
        const off = disabled(d.key);
        let style;
        let cls = 'h-11 rounded-[12px] text-[15px] font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ';
        // Graphite: a run day is a lime fill; the long-run day a lime outline
        if (on && tone === 'long') { cls += 'bg-transparent'; style = { border: `1.5px solid ${PROFILE_COLORS.longRun}`, color: PROFILE_COLORS.longRun }; }
        else if (on) cls += 'bg-accent text-on-accent';
        else if (off) { cls += 'bg-surface-2 border border-border cursor-not-allowed'; style = { color: PROFILE_COLORS.dayDisabled }; }
        else cls += 'bg-surface-2 border border-border text-text-muted';
        return (
          <button key={d.key} type="button" className={cls} style={style} disabled={off}
                  aria-pressed={on} aria-label={`${ariaPrefix} ${d.key}`} onClick={() => !off && onToggle(d.key)}>
            {d.label}
          </button>
        );
      })}
    </div>
  );
}

/** Level as 4 small vertical bars beside the text label (decorative: aria-hidden, the label carries the level).
 *  An unlisted level renders nothing — the meter never guesses a fill. */
const LEVEL_FILL = { beginner: 1, intermediate: 2, experienced: 2, advanced: 3, competitive: 4 };
export function LevelMeter({ level }) {
  const n = LEVEL_FILL[level];
  if (!n) return null;
  return (
    <span aria-hidden="true" data-testid="level-meter" className="inline-flex items-end gap-[2px] align-middle">
      {[1, 2, 3, 4].map((i) => (
        <span key={i} className="block w-[3px] h-[10px] rounded-[1px]"
              style={{ background: i <= n ? 'var(--g-accent)' : 'var(--g-bar)' }} />
      ))}
    </span>
  );
}

/** Single-choice segmented control (role=radiogroup, each option role=radio + aria-checked). options: [{value, label}] */
// The selected option uses the shared accent (the primary variant: bg-accent / text-on-accent) — one colour for every
// option, no border, no shadow, no status dot (owner 2026-11-01).
export const SEGMENT_SELECTED = 'bg-accent text-on-accent font-extrabold';
export const SEGMENT_UNSELECTED = 'bg-transparent text-text-body font-medium';

export function Segmented({ value, options, onChange, label }) {
  return (
    <div role="radiogroup" aria-label={label}
         className="grid grid-cols-3 gap-1 p-1 rounded-[14px] bg-surface-2 border border-border">
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button key={o.value} type="button" role="radio" aria-checked={on} onClick={() => onChange(o.value)}
                  data-selected={on ? 'true' : undefined}
                  className={`min-h-[48px] rounded-[10px] flex items-center justify-center text-[15px] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${on ? SEGMENT_SELECTED : SEGMENT_UNSELECTED}`}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** A row that expands its children (button + aria-expanded). Closed by default unless defaultOpen. */
export function Collapsible({ title, summary, children, defaultOpen = false, testId, extra }) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <div data-testid={testId}>
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}
              className="w-full min-h-[56px] flex items-center gap-3 text-right focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-control">
        <span className="flex-1 min-w-0">
          <span className="block text-[15px] font-semibold text-text">{title}</span>
          {summary && <span className="block text-[12px] text-text-muted truncate mt-0.5">{summary}</span>}
          {extra}
        </span>
        <span aria-hidden="true" className={`text-text-muted text-[14px] transition-transform ${open ? 'rotate-180' : ''}`}>⌄</span>
      </button>
      {open && <div id={id} className="pt-3">{children}</div>}
    </div>
  );
}

/** HR zones from the EXISTING calculation (60/70/80/90 % of max HR, same rounding as before). */
export function hrZones(maxHr) {
  const m = Number(maxHr);
  if (!m) return null;
  const r = (f) => Math.round(m * f);
  return [
    { name: 'Z1', range: `<${r(0.6)}` },
    { name: 'Z2', range: `${r(0.6)}-${r(0.7)}` },
    { name: 'Z3', range: `${r(0.7)}-${r(0.8)}` },
    { name: 'Z4', range: `${r(0.8)}-${r(0.9)}` },
    { name: 'Z5', range: `>${r(0.9)}` },
  ];
}

export function ZoneBar() {
  return (
    <span dir="ltr" aria-hidden="true" className="flex gap-0.5 mt-1.5 h-1 w-full max-w-[160px]">
      {PROFILE_COLORS.zones.map((c) => <span key={c} className="flex-1 rounded-full" style={{ background: c }} />)}
    </span>
  );
}

/** Fixed bottom bar, shown by the caller only when the running-profile form has unsaved changes. */
export function StickySaveBar({ onCancel, onSave, saving }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 bg-surface border-t border-border" role="region"
         aria-label="שינויים שלא נשמרו" data-testid="profile-save-bar">
      <div className="max-w-lg mx-auto px-4 py-3" dir="rtl">
        <p className="text-[13px] text-text-body leading-snug mb-2">יש שינויים בפרופיל הריצה שלא נשמרו</p>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onSave} disabled={saving}
                  className="flex-1 h-12 rounded-full btn-primary text-[15px]">
            {saving ? 'שומר…' : 'שמירה'}
          </button>
          <button type="button" onClick={onCancel} disabled={saving}
                  className="h-12 px-5 rounded-full border border-border text-text-body font-semibold text-[15px] disabled:opacity-50">
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
}
